import * as React from 'react'
import { Link, Prompt, useHistory } from 'react-router-dom'
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form'
import { useQuery, useMutation } from 'react-query'
import ReactSelect from 'react-select'
import { InputItem, Button, DatePicker, Toast } from 'antd-mobile'
import 'react-photo-view/dist/index.css'
import {
  getQusTypeList,
  getQusList,
  getFeedbackConfig,
  submitFeedbackForm,
  getQusTypeInfo
} from '@feedback/services/index'
import LeaveWarningModal from '@feedback/components/LeaveWarningModal'
import FormItemImages from '@feedback/pages/allfb/ticketForm/components/FormItemImage'
import FormItemVideo from '@feedback/pages/allfb/ticketForm/components/FormItemVideo'
import FormItemDescription from '@feedback/pages/allfb/ticketForm/components/FormItemDescription'
import {
  getTimeFormat,
  getTextByKey,
  reportFn,
  tracking,
  getLangKey
} from '@feedback/utils/util'
import pickBy from 'lodash/pickBy'
import styles from './index.module.scss'
import moment from 'moment'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import VestLayout from '@feedback/pages/allfb/vest/Layout'
import VestSelectSheet from '@feedback/components/VestSelectSheet'
import clsx from 'clsx'
import RichTextParser from './components/RichTextParser'

export interface IQusInfo {
  parent_qus: {
    _id: string
    id: number
    parent_id: number
    'en-US': string
    name: string
  }
  child_qus: {
    _id: string
    id: number
    parent_id: number
    'en-US': string
    name: string
  }
}

const getQusTypeInfoAll = async (qus_id: number): Promise<IQusInfo> => {
  const { code, data, message } = (await getQusTypeInfo({ qus_id })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return data
}

// 一级分类 Options
export interface IQusType {
  'en-US': string
  id: number
  _id: string
  name: string
}

// 处理过滤出 options 中的 name 为空的数据
const filterOptions = <T extends { name: string }>(options: T[]) => {
  return options.filter((item) => item?.name)
}

const getQusTypeOptions = async (): Promise<IQusType[]> => {
  const { code, data, message } = (await getQusTypeList()) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return Array.isArray(data) ? filterOptions(data) : []
}

// 二级问题 Options
export interface IQus {
  'en-US': string
  id: number
  _id: number
  name: string
}

const getQusOptions = async (qus_id: number): Promise<IQus[]> => {
  const { code, data, message } =
    (await getQusList(pickBy({ qus_id }, (x) => x || x === 0))) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return Array.isArray(data) ? filterOptions(data) : []
}

// 表单配置
export enum formType {
  string = 1,
  int = 2,
  date = 3,
  image = 4,
  email = 5,
  select = 6,
  video = 7,
  richText = 8
}

export interface IFormConfig {
  is_must: number
  key: string
  limit: number
  line: number
  name: string
  select: string[]
  type: number
  _id: string
  content?: string
}

export const getFormConfig = async (
  question_id?: string
): Promise<IFormConfig[]> => {
  const { code, data, message } =
    (await getFeedbackConfig({ question_id })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  if (!Array.isArray(data)) return []
  const textEditorIndex = data?.findIndex?.(
    (item: IFormConfig) => item.type === 8
  )
  const descriptionIndex = data?.findIndex?.(
    (item: IFormConfig) => item.key === 'content'
  )
  if (textEditorIndex !== -1 && descriptionIndex !== -1) {
    let [textEditorItem] = data.splice(textEditorIndex, 1) // 从数组中移除 Text Editor
    data.splice(descriptionIndex, 0, textEditorItem) // 将 Text Editor 插入到 Description 前面
  }
  return data
}

export const createFeedbackForm = async (params: any) => {
  const { code, data, message } = (await submitFeedbackForm(params)) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

const TicketForm: React.FC = () => {
  const history = useHistory()

  const { qus_id, question_id, faq_id, ...restURLParams } = Object.fromEntries(
    new URLSearchParams(window.location.href.split('?')[1])
  )

  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm()

  const handlerURLParamsToForm = React.useCallback(
    ({ formConfigs }: { formConfigs?: IFormConfig[] }) => {
      const restURLParamsKeys = Object.keys(restURLParams) || []
      formConfigs?.forEach?.((item) => {
        const allowType = [formType.string, formType.int]
        if (
          restURLParamsKeys.includes(item?.key) &&
          allowType.includes(item?.type) &&
          !watch(item?.key) &&
          restURLParams?.[item?.key]
        ) {
          setValue(item?.key, restURLParams[item?.key])
        }
      })
    },
    [restURLParams, watch, setValue]
  )

  const formConfig = useQuery(
    ['getFormConfig', [watch('qus_id.value'), watch('question_id.value')]],
    () => {
      if (getValues('question_id.value'))
        return getFormConfig(getValues('question_id.value'))

      if (getValues('qus_id.value'))
        return getFormConfig(getValues('qus_id.value'))
    },
    {
      onSuccess: (data) => {
        handlerURLParamsToForm({ formConfigs: data })
      }
    }
  )

  // 问题类型一级
  const qusTypeOptions = useQuery('getQusTypeOptions', getQusTypeOptions, {
    onSuccess: (data) => {
      const findItem = data?.find?.((item) => item?.id === Number(qus_id))
      if (findItem && !watch('qus_id')?.hasOwnProperty?.('value')) {
        setValue('qus_id', {
          value: findItem?.id,
          label: findItem?.name
        })
      }
    }
  })
  // 问题类型二级
  const qusOptions = useQuery(
    ['getQusOptions', watch('qus_id.value')],
    () => {
      if (watch('qus_id.value')) {
        return getQusOptions(getValues('qus_id.value'))
      }
    },
    {
      onSuccess: (data) => {
        const findItem = data?.find?.(
          (item) => item?.id === Number(question_id)
        )
        if (findItem && !watch('question_id')?.hasOwnProperty?.('value')) {
          setValue('question_id', {
            value: findItem?.id,
            label: findItem?.name
          })
        }
      }
    }
  )

  const submittingRef = React.useRef(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [modal, setModal] = React.useState({
    open: false,
    message: '',
    cancelText: '',
    onCancel: () => {},
    confirmText: '',
    onConfirm: () => {}
  })

  const alert = (value: any) => {
    setModal({
      ...value,
      onCancel: value.onCancel
        ? () => {
            if (value?.onCancel()) {
              setModal((val) => ({ ...val, open: false }))
            }
            return false
          }
        : false,
      onConfirm: value.onConfirm
        ? () => {
            if (value?.onConfirm()) {
              setModal((val) => ({ ...val, open: false }))
            }
            return false
          }
        : false
    })
  }

  const formSubmit = useMutation(createFeedbackForm, {
    onSuccess: async (data: any) => {
      if (appid === 'cm1006') {
        tracking({
          _step_id: '',
          _feedback_page_id: 6,
          _postion_id: 2,
          _search_words: '',
          _faq_id: faq_id,
          _src_faq_id: ''
        })
      } else {
        reportFn({
          parm1: 6,
          parm2: 2,
          parm5: 'h5_feedback_click',
          parm6: faq_id || ''
        })
      }
      setSubmitted(true)
      alert({
        open: true,
        message: getTextByKey('submit modal tip'),
        onConfirm: () => {
          reportFn({
            parm1: 6,
            parm2: 3,
            parm5: 'h5_feedback_click',
            parm6: faq_id || ''
          })
          setToPath('/ticketList?status=1')
          setConfirmLeave(true)
          return true
        }
      })
    },
    onError: async ({ message }: Error) => {
      Toast.info(
        `${getTextByKey('submit')} ${getTextByKey('fail')}${
          message ? ` , ${message}` : ''
        }!`
      )
    },
    onSettled: () => {
      submittingRef.current = false
    }
  })

  // 提交
  const onSubmit = (values: any) => {
    if (submittingRef.current) return
    if (
      !values?.qus_id?.value ||
      !values?.content ||
      !values?.question_id?.value
    ) {
      return
    }
    const keyConfigs = formConfig?.data
      ? formConfig.data.filter((item) => item.key.includes('key'))
      : []
    const keyValues = keyConfigs
      .map((item) => {
        let data = values[item.key]
        if (item.type === formType.int) {
          data = values?.[item.key]?.toString?.()
        }
        if (item.type === formType.date)
          data = values[item.key]
            ? moment(values[item.key]).format('YYYY-MM-DD HH:mm:ss')
            : ''
        if (item.type === formType.image || item.type === formType.video)
          data = values[`${item.key}_upload_key`]
        if (item.type === formType.select)
          data = values[item.key]?.label ? [values[item.key]?.label] : ''
        return data
          ? [
              item.key,
              {
                name: item.name,
                type: item.type,
                data: data || ''
              }
            ]
          : []
      })
      .filter(([key]) => key)
    const form = {
      qus_id: values.qus_id?.value,
      question_id: values.question_id?.value,
      content: values.content,
      email: values.email || '',
      ...(faq_id && { faq_id }),
      ...Object?.fromEntries?.(keyValues)
    }
    submittingRef.current = true
    formSubmit.mutate(form)
    reportFn({
      parm1: 6,
      parm2: 1,
      parm5: 'h5_feedback_click',
      parm6: faq_id || ''
    })
  }

  // 离开提示弹窗
  const [isConfirmLeave, setConfirmLeave] = React.useState(false)
  const [toPath, setToPath] = React.useState('')
  React.useEffect(() => {
    if (isConfirmLeave) {
      history.push(toPath)
    }
  }, [isConfirmLeave, history, toPath])

  const { appid, logo } = getAppConfig() || {}
  const isVest = isVestAppid(appid)
  const langKey = getLangKey()
  const headerFromApp = JSON.parse(
    sessionStorage.getItem('headerFromApp') || '{}'
  )

  const formContent = (
    <>
      {isVest ? (
        /* C1′-lite：顶栏仅 LOGO；标题/副标题落在 contact-intro 正文区 */
        <header className={clsx(styles.header, styles.vestIntro)}>
          <h2>{getTextByKey('Contact Us')}</h2>
          <p>
            {getTextByKey(
              'Please fill out the form with as many details as possible'
            )}
          </p>
        </header>
      ) : (
        <header className={clsx(styles.header)}>
          <h2>{getTextByKey('Contact Us')}</h2>
          <p>
            {getTextByKey(
              'Please fill out the form with as many details as possible'
            )}
          </p>
          <Link to='/'>
            <div className='logo' style={{ backgroundImage: `url(${logo})` }} />
          </Link>
        </header>
      )}
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <section className={styles['form-item']}>
            <label className='is-required'>
              {getTextByKey('feedback type')}
            </label>
            {!qusTypeOptions?.isLoading && (
              <Controller
                name='qus_id'
                control={control}
                rules={{ required: true }}
                shouldUnregister
                render={({ field }) => {
                  const selectFeedbackType = (
                    value: { value: any; label: any } | null
                  ) => {
                    if (getValues('qus_id.value') !== value?.value) {
                      setValue('question_id', {
                        value: undefined,
                        label: undefined
                      })
                    }
                    field.onChange(
                      value || { value: undefined, label: undefined }
                    )
                    if (value?.value) {
                      formConfig.refetch()
                      qusOptions.refetch()
                    }
                  }
                  const feedbackTypeOptions = Array.isArray(qusTypeOptions.data)
                    ? qusTypeOptions.data.map(({ id, name }) => ({
                        label: name,
                        value: id
                      }))
                    : []
                  return isVest ? (
                    <VestSelectSheet
                      value={field.value}
                      title={getTextByKey('feedback type')}
                      placeholder={getTextByKey('Select your feedback type')}
                      isError={Boolean(errors.qus_id)}
                      loading={
                        qusTypeOptions.isFetching ||
                        (formConfig.isFetching && !watch('question_id.value'))
                      }
                      options={feedbackTypeOptions}
                      onSelect={selectFeedbackType}
                    />
                  ) : (
                    <ReactSelect
                      {...field}
                      isSearchable={false}
                      isClearable={false}
                      isLoading={
                        qusTypeOptions.isFetching ||
                        (formConfig.isFetching && !watch('question_id.value'))
                      }
                      className={clsx(
                        styles.select,
                        errors.qus_id && 'is-error'
                      )}
                      classNamePrefix={'custom'}
                      placeholder={getTextByKey('Select your feedback type')}
                      onChange={(value, actionMeta) => {
                        if (
                          actionMeta.action === 'select-option' &&
                          getValues('qus_id.value') !== value?.value
                        ) {
                          setValue('question_id', {
                            value: undefined,
                            label: undefined
                          })
                        }
                        field.onChange(
                          value || {
                            value: undefined,
                            label: undefined
                          }
                        )
                        if (value?.value) {
                          formConfig.refetch()
                          qusOptions.refetch()
                        }
                      }}
                      options={feedbackTypeOptions}
                    />
                  )
                }}
              />
            )}
            {errors.qus_id && (
              <div className='error-message'>
                {getTextByKey('Cannot be blank')}
              </div>
            )}
          </section>

          {watch('qus_id.value') &&
          Array.isArray(qusOptions?.data) && (
            <section className={styles['form-item']}>
              <label className='is-required'>{getValues('qus_id.label')}</label>
              <Controller
                name='question_id'
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (!value?.value) return false
                    return true
                  }
                }}
                shouldUnregister
                render={({ field }) => {
                  const classificationPlaceholder =
                    ['cm1009', 'cm1017'].includes(appid) &&
                    headerFromApp?.language?.includes('fr')
                      ? getTextByKey('cm1009.fr.Select your {value}').replace(
                          '{value}',
                          getValues('qus_id')?.label
                        )
                      : getTextByKey('Select your {value}').replace(
                          '{value}',
                          getValues('qus_id')?.label
                        )
                  const classificationOptions = Array.isArray(qusOptions.data)
                    ? qusOptions.data.map(({ id, name }) => ({
                        value: id,
                        label: name
                      }))
                    : []
                  const selectClassification = (
                    value: { value: any; label: any } | null
                  ) => {
                    field.onChange(
                      value || { value: undefined, label: undefined }
                    )
                    if (value?.value) {
                      formConfig.refetch()
                    }
                  }
                  return isVest ? (
                    <VestSelectSheet
                      value={field.value}
                      title={getValues('qus_id')?.label}
                      placeholder={classificationPlaceholder}
                      isError={Boolean(errors.question_id)}
                      loading={qusOptions.isFetching || formConfig.isFetching}
                      options={classificationOptions}
                      onSelect={selectClassification}
                    />
                  ) : (
                    <ReactSelect
                      {...field}
                      isSearchable={false}
                      isClearable={field?.value?.label}
                      isLoading={qusOptions.isFetching || formConfig.isFetching}
                      className={clsx(
                        styles.select,
                        errors.question_id && 'is-error'
                      )}
                      classNamePrefix={'custom'}
                      onChange={selectClassification}
                      components={
                        field?.value?.label
                          ? {
                              DropdownIndicator: () => null,
                              IndicatorSeparator: () => null
                            }
                          : undefined
                      }
                      placeholder={classificationPlaceholder}
                      options={classificationOptions}
                    />
                  )
                }}
              />
              {errors?.question_id && (
                <div className='error-message'>
                  {getTextByKey('Cannot be blank')}
                </div>
              )}
            </section>
          )}

          {Array.isArray(formConfig.data) &&
            formConfig.data.map((item, index) => {
              if (!item?.key || !item?.type || item?.key === 'title')
                return null

              if (item?.type === formType.richText && item?.content) {
                return <RichTextParser html={item?.content} />
              }

              return (
                <section key={index} className={styles['form-item']}>
                  <label className={`${item?.is_must ? 'is-required' : ''}`}>
                    {item?.name}
                  </label>
                  <Controller
                    name={item?.key}
                    control={control}
                    defaultValue={restURLParams?.[item?.key] || undefined}
                    rules={{
                      required: Boolean(item?.is_must),
                      ...(item?.limit && { maxLength: item?.limit }),
                      ...(item?.type === formType.email && {
                        validate: (value) => {
                          if (value) {
                            const isEmail = /^[A-Za-z0-9]+([\.|\-|_]?[A-Za-z0-9]+)*\@[A-Za-z0-9]+([\.|\-|_]?[A-Za-z0-9]+)*\.[A-Za-z0-9]+$/.test(
                              value
                            )
                            if (isEmail) {
                              return true
                            }
                            if (
                              ['cm1009', 'cm1017'].includes(appid) &&
                              headerFromApp?.language?.includes('fr')
                            ) {
                              return getTextByKey(
                                'cm1009.fr.Please enter a valid email'
                              )
                            }
                            return getTextByKey('Please enter a valid email')
                          }
                          return true
                        }
                      })
                    }}
                    shouldUnregister
                    render={({ field }) => {
                      return (
                        <>
                          {item?.type === formType.string &&
                            item?.key === 'content' && (
                              <>
                                <FormItemDescription
                                  {...field}
                                  className={`${
                                    errors[item?.key] ? 'is-error' : ''
                                  }`}
                                  maxLimit={item?.limit}
                                  showCount
                                />
                                {Boolean(item?.is_must) &&
                                  errors[item?.key] &&
                                  errors[item?.key].type === 'required' && (
                                    <div className='error-message'>
                                      {getTextByKey('Cannot be blank')}
                                    </div>
                                  )}
                                {Boolean(item?.limit) &&
                                  errors[item?.key] &&
                                  errors[item?.key].type === 'maxLength' && (
                                    <div className='error-message'>
                                      {getTextByKey('Character limit exceeded')}
                                    </div>
                                  )}
                              </>
                            )}

                          {item?.type === formType.string &&
                            item?.key !== 'content' && (
                              <>
                                <InputItem
                                  {...field}
                                  maxLength={item?.limit}
                                  className={styles.input}
                                />
                                {Boolean(item?.is_must) &&
                                  errors[item?.key] &&
                                  errors[item?.key].type === 'required' && (
                                    <div className='error-message'>
                                      {getTextByKey('Cannot be blank')}
                                    </div>
                                  )}
                                {Boolean(item?.limit) &&
                                  errors[item?.key] &&
                                  errors[item?.key].type === 'maxLength' && (
                                    <div className='error-message'>
                                      {getTextByKey('Character limit exceeded')}
                                    </div>
                                  )}
                              </>
                            )}

                          {item?.type === formType.int && (
                            <>
                              <InputItem
                                {...field}
                                type={'number'}
                                maxLength={item?.limit}
                                className={styles.input}
                              />
                              {Boolean(item?.is_must) &&
                                errors[item?.key] &&
                                errors[item?.key].type === 'required' && (
                                  <div className='error-message'>
                                    {getTextByKey('Cannot be blank')}
                                  </div>
                                )}
                              {Boolean(item?.limit) &&
                                errors[item?.key] &&
                                errors[item?.key].type === 'maxLength' && (
                                  <div className='error-message'>
                                    {getTextByKey('Character limit exceeded')}
                                  </div>
                                )}
                            </>
                          )}

                          {item?.type === formType.date && (
                            <>
                              <DatePicker onChange={field.onChange} extra=' '>
                                <InputItem
                                  {...field}
                                  value={
                                    field.value
                                      ? getTimeFormat(field.value)
                                      : ''
                                  }
                                  className={styles.input}
                                />
                              </DatePicker>
                              {Boolean(item?.is_must) &&
                                errors[item?.key] &&
                                errors[item?.key].type === 'required' && (
                                  <div className='error-message'>
                                    {getTextByKey('Cannot be blank')}
                                  </div>
                                )}
                            </>
                          )}

                          {item?.type === formType.image && (
                            <>
                              <FormItemImages
                                {...field}
                                limit={item?.limit}
                                onChange={(file) => {
                                  field.onChange(file)
                                  if (file.length <= 0) {
                                    setValue(`${item?.key}_upload_key`, '')
                                  }
                                }}
                                httpResponse={(data) =>
                                  setValue(`${item?.key}_upload_key`, data)
                                }
                              />
                              {Boolean(item?.is_must) &&
                                errors[item?.key] &&
                                errors[item?.key].type === 'required' && (
                                  <div className='error-message'>
                                    {getTextByKey('Cannot be blank')}
                                  </div>
                                )}
                            </>
                          )}

                          {item?.type === formType.video && (
                            <>
                              <FormItemVideo
                                {...field}
                                limit={item?.limit}
                                onChange={(file) => {
                                  field.onChange(file)
                                  if (file.length <= 0) {
                                    setValue(`${item?.key}_upload_key`, '')
                                  }
                                }}
                                httpResponse={(data) =>
                                  setValue(`${item?.key}_upload_key`, data)
                                }
                              />
                              {Boolean(item?.is_must) &&
                                errors[item?.key] &&
                                errors[item?.key].type === 'required' && (
                                  <div className='error-message'>
                                    {getTextByKey('Cannot be blank')}
                                  </div>
                                )}
                            </>
                          )}

                          {item?.type === formType.email && (
                            <>
                              <InputItem {...field} className={styles.input} />
                              {Boolean(item?.is_must) &&
                                errors[item?.key]?.type === 'required' && (
                                  <div className='error-message'>
                                    {getTextByKey('Cannot be blank')}
                                  </div>
                                )}
                              {errors?.[item?.key]?.type === 'validate' && (
                                <div className='error-message'>
                                  {errors?.[item?.key]?.message}
                                </div>
                              )}
                            </>
                          )}

                          {item?.type === formType.select &&
                            (isVest ? (
                              <VestSelectSheet
                                value={field.value}
                                title={item?.name}
                                placeholder={getTextByKey(
                                  'Select your {value}'
                                ).replace('{value}', item?.name)}
                                isError={
                                  Boolean(item?.is_must) &&
                                  Boolean(errors[item?.key])
                                }
                                options={item?.select.map((val, idx) => ({
                                  value: idx,
                                  label: val
                                }))}
                                onSelect={(value) => field.onChange(value)}
                              />
                            ) : (
                              <ReactSelect
                                {...field}
                                isSearchable={false}
                                isClearable={field?.value?.label}
                                className={clsx(styles.select)}
                                classNamePrefix={'custom'}
                                placeholder={''}
                                options={item?.select.map((val, idx) => ({
                                  value: idx,
                                  label: val
                                }))}
                                components={
                                  field?.value?.label
                                    ? {
                                        DropdownIndicator: () => null,
                                        IndicatorSeparator: () => null
                                      }
                                    : undefined
                                }
                              />
                            ))}
                          {item?.type === formType.select &&
                            Boolean(item?.is_must) &&
                            errors[item?.key] &&
                            errors[item?.key].type === 'required' && (
                              <div className='error-message'>
                                {getTextByKey('Cannot be blank')}
                              </div>
                            )}
                        </>
                      )
                    }}
                  />
                </section>
              )
            })}

          <Button
            loading={formSubmit.isLoading}
            disabled={formSubmit.isLoading || formConfig.isFetching}
            className={clsx(styles.button)}
            onClick={() => handleSubmit(onSubmit)()}
          >
            {/* antd-mobile v2 Button 的 insertSpace 读 child.type，
                falsy child（false/null）会直接 TypeError，禁止条件短路写法 */}
            {isVest ? (
              getTextByKey('submit')
            ) : (
              <>
                <i className='icon' />
                {getTextByKey('submit')}
              </>
            )}
          </Button>
        </form>
    </>
  )

  return (
    <>
      {isVest ? (
        <VestLayout
          showBottomBar={false}
          showSearch={false}
          bodyClassName={styles.vestBody}
        >
          <section
            className={clsx(
              styles.container,
              styles.vest,
              langKey === 'ar-SA' && styles['rtl-mode']
            )}
          >
            {formContent}
          </section>
        </VestLayout>
      ) : (
        <section
          className={clsx(
            styles.container,
            styles?.[appid],
            langKey === 'ar-SA' && styles['rtl-mode']
          )}
        >
          {formContent}
        </section>
      )}

      <Prompt
        message={({ pathname, search }) => {
          if (submitted) return true

          if (!isConfirmLeave && watch('qus_id.value')) {
            setToPath(`${pathname}${search}` || '/')
            alert({
              open: true,
              message: getTextByKey(
                'You will not be able to receive a reply after it is closed'
              ),
              cancelText: getTextByKey('leave'),
              onCancel: () => {
                setConfirmLeave(true)
                return true
              },
              confirmText: getTextByKey('stay'),
              onConfirm: () => true
            })
            return false
          }
          return true
        }}
      />

      <LeaveWarningModal {...modal} />
    </>
  )
}

export default TicketForm
